import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Chip,
  IconButton
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Close,
  AutoAwesome
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { apiClient } from '@/services/api';

interface ProjectSelfEvaluationIndicatorAIImportDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onImportSuccess: () => void;
}

const ProjectSelfEvaluationIndicatorAIImportDialog: React.FC<ProjectSelfEvaluationIndicatorAIImportDialogProps> = ({
  open,
  onClose,
  projectId,
  onImportSuccess
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResult, setImportResult] = useState<any>(null);

  // 文件拖拽处理
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      const fileName = selectedFile.name.toLowerCase();

      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        return;
      }

      setFile(selectedFile);
      setImportResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  // 处理AI导入
  const handleImport = async () => {
    if (!file) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // 模拟上传进度
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 300);

      // 调用AI导入API
      const response = await apiClient.upload(
        `/projects/${projectId}/project-self-evaluation-indicators/import-excel-ai`,
        file,
        (progress) => {
          setUploadProgress(Math.min(progress, 90));
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        // 导入成功，直接刷新数据并关闭对话框
        onImportSuccess();
        handleClose();
      } else {
        throw new Error(response.message || 'AI识别导入失败');
      }
    } catch (error: any) {
      setImportResult({
        success: false,
        error: error.response?.data?.detail || error.message || 'AI识别导入失败'
      });
    } finally {
      setIsUploading(false);
    }
  };

  // 重置状态
  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setUploadProgress(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <AutoAwesome sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">AI智能识别导入</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* 说明 */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>AI智能识别</strong>：自动识别Excel表格结构，提取项目信息、资金执行情况、资金管理情况和绩效指标数据。
            支持各种格式的表格，无需固定列名。
          </Typography>
        </Alert>

        {!file && (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? '松开文件以上传' : '拖拽文件到此处或点击选择文件'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              支持 .xlsx, .xls 格式
            </Typography>
          </Box>
        )}

        {file && (
          <Box sx={{ mt: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center">
                <Description sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1" fontWeight="medium">
                  {file.name}
                </Typography>
              </Box>
              <Chip
                icon={<AutoAwesome />}
                label="AI识别"
                color="primary"
                size="small"
              />
            </Box>

            {isUploading && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  正在进行AI识别...
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  {uploadProgress}%
                </Typography>
              </Box>
            )}

            {importResult && !importResult.success && (
              <Alert severity="error" sx={{ mt: 2 }}>
                AI识别失败：{importResult.error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          取消
        </Button>
        {file && (
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={isUploading}
            startIcon={<AutoAwesome />}
          >
            {isUploading ? 'AI识别中...' : 'AI智能识别'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProjectSelfEvaluationIndicatorAIImportDialog;
